/** @format */

import type React from "react";
import type { ReactNode } from "react";

import type { AppConfig } from "@/api/configApi";

import { useConfigStore, useSyncConfigStore } from "@/stores/configStore";

interface ConfigContextValue {
  config: AppConfig | undefined;
  isLoading: boolean;
  error: Error | null;
}

interface ConfigProviderProps {
  children: ReactNode;
}

/**
 * Provider component that makes runtime configuration available throughout the app.
 * Configuration is fetched from the /config endpoint and cached for 5 minutes.
 * Note: Runtime config values are separate from build-time VITE_* variables.
 *
 * This provider syncs React Query data with the zustand store for consistent state management.
 */
export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  // Sync React Query data with zustand store
  // This hook must be called unconditionally (Rules of Hooks)
  useSyncConfigStore();

  return <>{children}</>;
};

/**
 * Hook to access the config from zustand store.
 * This replaces the old context-based hook for consistency with other stores.
 * Uses a single selector to prevent multiple re-renders.
 */
export const useAppConfig = (): ConfigContextValue => {
  return useConfigStore(state => ({
    config: state.config,
    isLoading: state.isLoading,
    error: state.error,
  }));
};

/**
 * Convenience hook to get runtime local environment detection.
 * Note: This is separate from build-time VITE_LOCAL variable.
 * Uses direct selector for optimal performance.
 */
export const useIsLocalRuntime = (): boolean | undefined => {
  return useConfigStore(state => state.config?.isLocal);
};

/**
 * Convenience hook to get server-side version from config.
 * Note: This is separate from build-time VITE_VERSION variable.
 * Uses direct selector for optimal performance.
 */
export const useConfigServerVersion = (): string | undefined => {
  return useConfigStore(state => state.config?.version);
};

/**
 * Convenience hook to get runtime environment string.
 * Uses direct selector for optimal performance.
 */
export const useAppEnvironment = (): string | undefined => {
  return useConfigStore(state => state.config?.environment);
};

/**
 * Convenience hook to get timezone.
 * Uses direct selector for optimal performance.
 */
export const useAppTimezone = (): string | undefined => {
  return useConfigStore(state => state.config?.timezone);
};

/**
 * Convenience hook to get list of available series.
 * Uses direct selector for optimal performance.
 */
export const useAvailableSeries = (): string[] | undefined => {
  return useConfigStore(state => state.config?.availableSeries);
};
